/**
 * Fleet dashboard map markers — Leaflet L.divIcon factories.
 *
 * Each status gets a distinct 32x32 SVG shape rendered inline so there are
 * zero network requests and the icons scale perfectly on retina displays.
 *
 * Usage:
 *   import { getFleetMarkerIcon } from '../../assets/markers/fleetMarkers';
 *   L.marker([lat, lng], { icon: getFleetMarkerIcon(vehicle) })
 */
import L from 'leaflet';
import markerSprite from '../marker_sprite.png';

// ── Status colors (matches STATUS_CONFIG in TruckTrackingDashboard) ──
const COLORS = {
  DRIVING: '#1E88E5',
  ON_SITE: '#2E7D32',
  AT_TRACKED_LOCATION: '#EF6C00',
  IDLE_OTHER: '#C62828',
  AT_YARD: '#6D6D6D',
  DEFAULT: '#607D8B',
};

const STROKE = '#1B1B1B';
const SIZE = 32;
const ANCHOR = [SIZE / 2, SIZE]; // tip of pin
const POPUP_ANCHOR = [0, -SIZE];

// ── SVG templates (32x32 viewBox) ────────────────────────────────────

/**
 * DRIVING — arrow/chevron pointing up, rotated by vehicle bearing.
 * Distinctive from the stationary pins so it's instantly recognisable.
 */
const drivingSvg = (color, rotation = 0) => `
<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 32 32">
  <g transform="rotate(${rotation} 16 16)">
    <path d="M16 2 L28 26 L16 20 L4 26 Z"
          fill="${color}" fill-opacity="0.95"
          stroke="${STROKE}" stroke-width="1.5" stroke-linejoin="round"/>
  </g>
</svg>`;

/**
 * Stationary pin — teardrop/map-pin shape with an inner icon glyph.
 * The glyph distinguishes the status at a glance.
 */
const pinSvg = (color, glyph) => `
<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 32 32">
  <path d="M16 1 C9.4 1 4 6.1 4 12.2 C4 20.5 16 31 16 31 C16 31 28 20.5 28 12.2 C28 6.1 22.6 1 16 1Z"
        fill="${color}" fill-opacity="0.92"
        stroke="${STROKE}" stroke-width="1.4"/>
  <circle cx="16" cy="12" r="8" fill="white" fill-opacity="0.25"/>
  ${glyph}
</svg>`;

// ── Inner glyphs (white, centered in the pin's circle) ──────────────

// ON_SITE: checkmark — productive work
const glyphOnSite = `
  <path d="M11.5 12.5 L14.5 15.5 L20.5 9.5"
        fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`;

// AT_TRACKED_LOCATION: exclamation — flagged stop
const glyphTracked = `
  <line x1="16" y1="7" x2="16" y2="13"
        stroke="white" stroke-width="2.2" stroke-linecap="round"/>
  <circle cx="16" cy="16.5" r="1.2" fill="white"/>`;

// IDLE_OTHER: clock — sitting idle
const glyphIdle = `
  <circle cx="16" cy="12" r="5.5" fill="none" stroke="white" stroke-width="1.8"/>
  <line x1="16" y1="9" x2="16" y2="12" stroke="white" stroke-width="1.8" stroke-linecap="round"/>
  <line x1="16" y1="12" x2="18.5" y2="13.5" stroke="white" stroke-width="1.8" stroke-linecap="round"/>`;

// AT_YARD: home/garage — back at base
const glyphYard = `
  <path d="M10 14 L16 8 L22 14" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.5 13 L11.5 17.5 L20.5 17.5 L20.5 13" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`;

// DEFAULT: dot
const glyphDefault = `<circle cx="16" cy="12" r="3" fill="white"/>`;

// ── Icon factory ─────────────────────────────────────────────────────

const iconCache = new Map();

const createIcon = (html) =>
  L.divIcon({
    html,
    className: 'fleet-marker', // bare class — no default leaflet icon styles
    iconSize: [SIZE, SIZE],
    iconAnchor: ANCHOR,
    popupAnchor: POPUP_ANCHOR,
  });

/**
 * Return a Leaflet divIcon for a vehicle object.
 *
 * @param {Object} vehicle
 * @param {string} vehicle.status  - DRIVING | ON_SITE | AT_TRACKED_LOCATION | IDLE_OTHER | AT_YARD
 * @param {number} [vehicle.bearing] - heading in degrees (used for DRIVING arrow rotation)
 * @returns {L.DivIcon}
 */
export const getFleetMarkerIcon = (vehicle) => {
  const status = vehicle?.status || 'DEFAULT';
  const color = COLORS[status] || COLORS.DEFAULT;

  // DRIVING icons are unique per bearing (rounded to 5-degree steps to limit cache entries)
  if (status === 'DRIVING') {
    const bearing = Math.round((Number(vehicle?.bearing) || 0) / 5) * 5;
    const key = `DRIVING_${bearing}`;
    if (!iconCache.has(key)) {
      iconCache.set(key, createIcon(drivingSvg(color, bearing)));
    }
    return iconCache.get(key);
  }

  // Stationary icons are cached by status only
  if (iconCache.has(status)) {
    return iconCache.get(status);
  }

  let glyph;
  switch (status) {
    case 'ON_SITE':
      glyph = glyphOnSite;
      break;
    case 'AT_TRACKED_LOCATION':
      glyph = glyphTracked;
      break;
    case 'IDLE_OTHER':
      glyph = glyphIdle;
      break;
    case 'AT_YARD':
      glyph = glyphYard;
      break;
    default:
      glyph = glyphDefault;
  }

  const icon = createIcon(pinSvg(color, glyph));
  iconCache.set(status, icon);
  return icon;
};

// ── Numbered stop icon factory ───────────────────────────────────────

const STOP_SIZE = 28;
const stopIconCache = new Map();
const timelineSpriteIconCache = new Map();
const TIMELINE_SPRITE_SIZE = 24;
const TIMELINE_SPRITE_POSITIONS = {
  DRIVING: '0% 0%',
  JOBSITE: '50% 0%',
  TRACKED_LOCATION: '100% 0%',
  IDLE_OTHER: '0% 50%',
  YARD: '50% 50%',
  SCHEDULED_ARRIVAL: '100% 50%',
  SCHEDULED_DEPARTURE: '0% 100%',
  WORK_TICKET_COMPLETED: '50% 100%',
  DEFAULT: '100% 100%',
};

const numberedStopSvg = (number, color) => `
<svg xmlns="http://www.w3.org/2000/svg" width="${STOP_SIZE}" height="${STOP_SIZE}" viewBox="0 0 28 28">
  <circle cx="14" cy="14" r="13" fill="${color}" stroke="#fff" stroke-width="2"/>
  <text x="14" y="14" text-anchor="middle" dominant-baseline="central"
        fill="#fff" font-family="Arial,sans-serif" font-size="13" font-weight="bold">
    ${number}
  </text>
</svg>`;

/**
 * Return a Leaflet divIcon for a numbered stop marker.
 *
 * @param {number} number - Stop number (1, 2, 3…)
 * @param {string} color  - Background color for the circle
 * @returns {L.DivIcon}
 */
export const getNumberedStopIcon = (number, color) => {
  const key = `${number}_${color}`;
  if (!stopIconCache.has(key)) {
    stopIconCache.set(
      key,
      L.divIcon({
        html: numberedStopSvg(number, color),
        className: 'fleet-marker',
        iconSize: [STOP_SIZE, STOP_SIZE],
        iconAnchor: [STOP_SIZE / 2, STOP_SIZE / 2],
        popupAnchor: [0, -STOP_SIZE / 2],
      }),
    );
  }
  return stopIconCache.get(key);
};

/**
 * Return a Leaflet divIcon that crops from the timeline sprite-sheet.
 *
 * @param {string} markerType - Canonical marker type, e.g. SCHEDULED_ARRIVAL
 * @returns {L.DivIcon}
 */
export const getTimelineSpriteIcon = (markerType) => {
  const normalized = `${markerType || 'DEFAULT'}`.toUpperCase();
  const spritePos = TIMELINE_SPRITE_POSITIONS[normalized] || TIMELINE_SPRITE_POSITIONS.DEFAULT;
  const key = `timeline_sprite_${normalized}`;
  if (!timelineSpriteIconCache.has(key)) {
    const html = `
<div style="
  width:${TIMELINE_SPRITE_SIZE}px;
  height:${TIMELINE_SPRITE_SIZE}px;
  background-image:url('${markerSprite}');
  background-size:300% 300%;
  background-position:${spritePos};
  background-repeat:no-repeat;
  filter:drop-shadow(0 1px 2px rgba(0,0,0,0.25));
"></div>`;
    timelineSpriteIconCache.set(
      key,
      L.divIcon({
        html,
        className: 'fleet-marker',
        iconSize: [TIMELINE_SPRITE_SIZE, TIMELINE_SPRITE_SIZE],
        iconAnchor: [TIMELINE_SPRITE_SIZE / 2, TIMELINE_SPRITE_SIZE],
        popupAnchor: [0, -TIMELINE_SPRITE_SIZE],
      }),
    );
  }
  return timelineSpriteIconCache.get(key);
};

/**
 * Bare CSS to strip Leaflet's default divIcon background/border.
 */
export const fleetMarkerCSS = `
.fleet-marker,
.leaflet-div-icon.fleet-marker {
  background: none !important;
  border: none !important;
  box-shadow: none !important;
}
@keyframes highlightPulse {
  0%, 100% { opacity: 0.9; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.4); }
}
.highlight-pulse {
  animation: highlightPulse 1.2s ease-in-out infinite;
}
`;

// Inject CSS immediately on module load so it's ready before any markers render
if (typeof document !== 'undefined' && !document.getElementById('fleet-marker-css')) {
  const style = document.createElement('style');
  style.id = 'fleet-marker-css';
  style.textContent = fleetMarkerCSS;
  document.head.appendChild(style);
}

export default getFleetMarkerIcon;
